/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { NotificationViewStyle } from '@airasia-phoenix/react-native/components/NotificationView';
import { useCallback, useState } from 'react';
import { useSsoErrorReporter } from './useSsoErrorReporter';
import { SCREEN_NAME } from '../config/analytics';
import { useTranslation } from 'react-i18next';
import { CANCEL_DELETION_STATUS } from '../constants/accountRecovery';
import { isPasskeySupported } from '../managers/passkeyManager';
import { describeRestoreError } from '../utils/accountRecovery';
import { isSocialAuthCancelled } from '../utils/socialSignIn';
import { describeSocialAuthError } from '../utils/ssoErrors';
import {
  LoginData,
  LoginError,
  LoginNotification,
} from '../widgets/LoginView/types';
import type { KeepAccountRecoveryHandlers } from './useKeepAccountRecovery';
import { useLoginByPasskey } from './passkey';

export interface UsePasskeyLoginFlowOptions {
  onLoginSuccess?: (data: LoginData) => void | Promise<void>;
  onLoginError?: (errorData: LoginError) => void;
  setNotification: (notification: LoginNotification | null) => void;
  accountRecovery?: KeepAccountRecoveryHandlers;
}

/** Passkey sign-in, plus whether this device supports it at all. */
export function usePasskeyLoginFlow(options: UsePasskeyLoginFlowOptions) {
  const { onLoginSuccess, onLoginError, setNotification, accountRecovery } =
    options;

  const { t } = useTranslation('account');

  const { isPending, mutateAsync: passkeyLoginMutate } = useLoginByPasskey();
  // isPending covers the request only; the sign-in tail inside onLoginSuccess
  // (profile check, host handoff, navigation) runs after it and must be covered
  // too, or the loader blinks off while this screen is still on top.
  const [isFinishing, setIsFinishing] = useState(false);
  const passkeySupported = isPasskeySupported();
  const reportError = useSsoErrorReporter(SCREEN_NAME.loginV2);

  const handlePasskeyLogin = useCallback(async () => {
    setIsFinishing(true);
    try {
      setNotification(null);
      await passkeyLoginMutate({});
      // Navigated; this screen unmounts, so the loader is left up on purpose.
      await onLoginSuccess?.({ method: 'passkey' });
    } catch (error) {
      setIsFinishing(false);
      if (isSocialAuthCancelled(error)) {
        return;
      }
      const errorMessage = describeSocialAuthError(
        error,
        t,
        t('socialAuthErrors.oops'),
      ).message;
      reportError(error, errorMessage);
      onLoginError?.({ method: 'passkey', error: errorMessage });

      if (
        accountRecovery?.tryHandleScheduledDeletion(error, () => {
          void (async () => {
            setIsFinishing(true);
            try {
              await passkeyLoginMutate({ status: CANCEL_DELETION_STATUS });
              await onLoginSuccess?.({ method: 'passkey' });
            } catch (retryError) {
              setIsFinishing(false);
              if (isSocialAuthCancelled(retryError)) {
                return;
              }
              const message = describeRestoreError(
                retryError,
                t,
                'loginView.loginFailed',
              );
              reportError(retryError, message);
              setNotification({
                message,
                type: NotificationViewStyle.ERROR,
              });
            }
          })();
        })
      ) {
        setNotification(null);
        return;
      }

      setNotification({
        message: errorMessage,
        type: NotificationViewStyle.ERROR,
      });
    }
  }, [
    accountRecovery,
    onLoginError,
    onLoginSuccess,
    passkeyLoginMutate,
    reportError,
    setNotification,
    t,
  ]);

  return {
    handlePasskeyLogin,
    isPasskeyLoginPending: isPending || isFinishing,
    passkeySupported,
  };
}
